import { Button, TextField, Tooltip, Typography } from '@material-ui/core';
import { firestore } from 'firebase';
import { compact } from 'lodash';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useScrollIntoView } from '../../hooks/useScrollIntoView';
import {
  editCommentOrReply,
  getNestedReplyCount,
  isSameActor,
  pinComment,
  publishComment,
  removeCommentOrReply,
  toCommentIdHash,
  unpinComment,
  useCommentHierarchyForEntity
} from '../../services/comments';
import { useCurrentUser } from '../../services/currentUser';
import { EMPTY_ARR } from '../../services/emptyConstants';
import { useUserPublicById } from '../../services/usersPublic';
import styled from '../../styled';
import {
  CommentActor,
  CommentActorGuest,
  CommentActorUser,
  CommentRemoveReason,
  CommentStatus,
  IComment,
  ICommentWithReplies
} from '../../types/Comment';
import { Doc, DocReference } from '../../types/Document';
import { CurrentUser } from '../../types/User';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../AdditionalActionsMenu';
import { Blockquote } from '../Blockquote';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { withDeletionConfirmation } from '../DeletionConfirmationDialog';
import { LinkExternal } from '../LinkExternal';
import { PlainLoader } from '../Loader';
import {
  AvatarWithNameAndMetadataLoader,
  BadgeProps,
  GuestAvatarWithNameAndMetadata,
  UserAvatarWithNameAndMetadata
} from '../UserAvatar';
import { GuestCommentForm, UserCommentForm } from './CommentForm';

const PinIcon = require('../../assets/pin.svg') as string;

type Props = {
  entityRef: DocReference;
};

const Container = styled('div')``;

const Header = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const HeaderSection = styled('div')((p) => ({
  display: 'flex',
  justifyContent: '',
  alignItems: 'center',
  '> :not(:first-child)': {
    marginLeft: p.theme.spacing(1)
  }
}));

const Body = styled('div')((p) => ({
  marginLeft: p.theme.spacing(3),
  borderLeft: `1px solid ${p.theme.palette.grey[500]}`,
  paddingTop: p.theme.spacing(2),
  paddingLeft: p.theme.spacing(2),
  paddingBottom: p.theme.spacing(3),

  '> :not(:first-child)': {
    marginTop: p.theme.spacing(1)
  }
}));

const InnerBody = styled('div')((p) => ({
  marginLeft: p.theme.spacing(2)
}));

const Replies = styled('div')((p) => ({
  marginLeft: p.theme.spacing(1)
}));

const CommentDates = ({
  createdAt,
  editedAt
}: {
  createdAt: firestore.Timestamp;
  editedAt: firestore.Timestamp | null;
}) => {
  const created = DateTime.fromMillis(createdAt.toMillis());
  const edited =
    editedAt && !createdAt.isEqual(editedAt)
      ? DateTime.fromMillis(editedAt.toMillis())
      : null;

  return (
    <Typography
      component="div"
      variant="subtitle2"
      style={{ fontStyle: 'italic' }}
    >
      <Tooltip
        title={`posted at ${created.toLocaleString(DateTime.DATETIME_SHORT)}`}
        placement="top"
      >
        <span>{created.toRelative()}</span>
      </Tooltip>
      {edited && (
        <span>
          {' '}
          <Tooltip title={edited.toLocaleString(DateTime.DATETIME_SHORT)}>
            <span>(edited {edited.toRelative()})</span>
          </Tooltip>
        </span>
      )}
    </Typography>
  );
};

const CommentCreatorUser = ({
  d,
  additionalBadges,
  createdAt,
  editedAt
}: {
  d: CommentActorUser;
  additionalBadges: BadgeProps[];
  createdAt: firestore.Timestamp;
  editedAt: firestore.Timestamp | null;
}) => {
  const [user, loading] = useUserPublicById(d.id);
  if (loading) {
    return <AvatarWithNameAndMetadataLoader />;
  }

  if (user) {
    return (
      <UserAvatarWithNameAndMetadata
        user={user.data}
        additionalBadges={additionalBadges}
        nextToName={<CommentDates createdAt={createdAt} editedAt={editedAt} />}
      />
    );
  }
  return null;
};

const CommentCreatorGuest = ({
  d,
  additionalBadges,
  createdAt,
  editedAt
}: {
  d: CommentActorGuest;
  additionalBadges: BadgeProps[];
  createdAt: firestore.Timestamp;
  editedAt: firestore.Timestamp | null;
}) => {
  return (
    <GuestAvatarWithNameAndMetadata
      name={d.name}
      website={d.website}
      additionalBadges={additionalBadges}
      nextToName={<CommentDates createdAt={createdAt} editedAt={editedAt} />}
    />
  );
};

export const CommentCreator = ({ d }: { d: Doc<IComment> }) => {
  const { createdBy, createdAt, editedAt, status } = d.data;
  const additionalBadges: BadgeProps[] = useMemo(
    () =>
      status === CommentStatus.PENDING ? [PENDING_MODERATION_BADGE] : EMPTY_ARR,
    [status]
  );
  return createdBy.type === 'USER' ? (
    <CommentCreatorUser
      d={createdBy}
      createdAt={createdAt}
      editedAt={editedAt}
      additionalBadges={additionalBadges}
    />
  ) : (
    <CommentCreatorGuest
      d={createdBy}
      createdAt={createdAt}
      editedAt={editedAt}
      additionalBadges={additionalBadges}
    />
  );
};

const ReplyFormContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(4),
  marginBottom: p.theme.spacing(4),
  marginLeft: p.theme.spacing(1)
}));

const ReplyForm = ({
  entityRef,
  parentRef,
  onSuccess,
  onCancel,
  autoFocus
}: {
  entityRef: DocReference;
  parentRef: DocReference;
  onSuccess: (d: Doc<IComment>) => void;
  onCancel: () => void;
  autoFocus?: boolean;
}) => {
  const { user } = useCurrentUser();
  const ref = useScrollIntoView<HTMLDivElement>(true, {
    block: 'center'
  });
  return (
    <div ref={ref}>
      <ReplyFormContainer>
        {user ? (
          <UserCommentForm
            user={user}
            entityRef={entityRef}
            parentRef={parentRef}
            onCancel={onCancel}
            onSuccess={onSuccess}
            autoFocus={autoFocus}
          />
        ) : (
          <GuestCommentForm
            entityRef={entityRef}
            parentRef={parentRef}
            onCancel={onCancel}
            onSuccess={onSuccess}
            autoFocus={autoFocus}
          />
        )}
      </ReplyFormContainer>
    </div>
  );
};

const ReplyButtonInner = styled(Button)((p) => ({
  fontWeight: p.theme.typography.fontWeightBold
}));

const ReplyButton = ({ onClick }: { onClick: () => void }) => {
  return <ReplyButtonInner onClick={onClick}>Reply</ReplyButtonInner>;
};

const EditActions = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  '> :not(:first-child)': {
    marginLeft: p.theme.spacing(2)
  },
  marginTop: p.theme.spacing(2)
}));

const EditForm = ({
  user,
  commentId,
  originalBody,
  onSuccess,
  onCancel
}: {
  user: CurrentUser;
  commentId: string;
  originalBody: string;
  onSuccess: () => void;
  onCancel: () => void;
}) => {
  const [body, setBody] = useState(originalBody);
  const { enqueueSnackbar } = useSnackbar();
  return (
    <>
      <TextField
        value={body}
        onChange={(ev) => setBody(ev.target.value)}
        multiline
        fullWidth
        rows={4}
        variant="outlined"
        autoFocus={true}
      />
      <EditActions>
        <Button onClick={onCancel}>Cancel</Button>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending="Editing..."
          disabled={!body}
          onClick={() => {
            return editCommentOrReply(
              {
                type: 'USER',
                id: user.id
              },
              commentId,
              body
            )
              .then(onSuccess)
              .catch(() => {
                enqueueSnackbar('Something went wrong! Please try again.', {
                  variant: 'error'
                });
              });
          }}
        >
          Edit
        </ButtonWithPromise>
      </EditActions>
    </>
  );
};

const Comments = ({
  ds,
  entityRef,
  scrollTo
}: {
  ds: Doc<ICommentWithReplies>[];
  entityRef: DocReference;
  scrollTo?: string;
}) => {
  useEffect(() => {
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [scrollTo]);

  return (
    <>
      {ds.map((d) => (
        <Comment key={d.id} d={d} entityRef={entityRef} />
      ))}
    </>
  );
};

const BlueClip = styled('div')((p) => ({
  borderRadius: '50%',
  background: p.theme.palette.primary.dark,
  height: p.theme.spacing(4),
  width: p.theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    width: '18px'
  }
}));

const Pin = () => {
  return (
    <Tooltip title="Pinned to the top of this thread">
      <BlueClip>
        <PinIcon />
      </BlueClip>
    </Tooltip>
  );
};

const RemovedBody = ({ d }: { d: Doc<IComment> }) => {
  if (d.data.removedBy) {
    if (isSameActor(d.data.removedBy, d.data.createdBy)) {
      return <i>This comment was removed by its author.</i>;
    }
  }
  if (d.data.removeReason === CommentRemoveReason.VIOLATION) {
    return <i>This comment violated our guideslines and was removed.</i>;
  }

  return <i>This comment was removed.</i>;
};

const toMenuOptions = (
  d: Doc<IComment>,
  user: CurrentUser | null,
  goToEditMode: () => void
): AdditionActionsMenuOption[] => {
  const isRoot = !d.data.parentRef;
  const { pinned } = d.data;
  const actor = (): CommentActor => ({
    type: 'USER',
    id: user!.id
  });
  const isAuthor =
    d.data.createdBy.type === 'USER' && d.data.createdBy.id === user?.id;
  const options: (AdditionActionsMenuOption | undefined | false)[] = [
    (user?.isAdmin || isAuthor) &&
      d.data.status !== CommentStatus.REMOVED && {
        key: 'edit',
        label: 'Edit comment',
        description: 'Edit the content of this comment',
        onClick: () => goToEditMode()
      },
    user?.isAdmin &&
      d.data.status === CommentStatus.PENDING && {
        key: 'approve',
        label: 'Approve comment',
        description: 'Publish this comment and make it public',
        onClick: () => publishComment(actor(), d.id)
      },
    user?.isAdmin &&
      isRoot &&
      !pinned && {
        key: 'pin',
        label: 'Pin to top',
        description: 'Pin this comment to the top of the thread',
        onClick: () => pinComment(actor(), d.id)
      },
    user?.isAdmin &&
      isRoot &&
      pinned && {
        key: 'pin',
        label: 'Unpin from top',
        description: 'Unpin this comment from the top of the thread',
        onClick: () => unpinComment(d.id)
      },
    (user?.isAdmin || isAuthor) &&
      d.data.replyCount === 0 && {
        key: 'remove',
        label: 'Remove comment',
        description: 'Remove this comment and leave no trace of it',
        dialog: withDeletionConfirmation(() => removeCommentOrReply(actor(), d))
      },
    (user?.isAdmin || isAuthor) &&
      d.data.replyCount > 0 &&
      d.data.status !== CommentStatus.REMOVED && {
        key: 'remove',
        label: 'Remove comment',
        description:
          'As this comment already has replies, the comment will remain and only its content will be removed.',
        dialog: withDeletionConfirmation(() =>
          removeCommentOrReply(actor(), d, CommentRemoveReason.NONE)
        )
      },
    user?.isAdmin &&
      d.data.replyCount > 0 &&
      d.data.status !== CommentStatus.REMOVED && {
        key: 'remove',
        label: 'Remove comment (violation)',
        description:
          'Remove the content of this comment and mark it as a violation.',
        dialog: withDeletionConfirmation(() =>
          removeCommentOrReply(actor(), d, CommentRemoveReason.VIOLATION)
        )
      }
  ];
  return compact(options);
};

const PENDING_MODERATION_BADGE: BadgeProps = {
  text: 'Pending Moderation',
  color: 'yellow',
  variant: 'contained'
};

const Comment = ({
  entityRef,
  d
}: {
  entityRef: DocReference;
  d: Doc<ICommentWithReplies>;
}) => {
  const { user } = useCurrentUser();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const goToEditMode = useCallback(() => setEditMode(true), []);
  const parentRef: DocReference = useMemo(
    () => ({ collection: d.collection, id: d.id }),
    [d]
  );
  const options: AdditionActionsMenuOption[] = useMemo(
    () => toMenuOptions(d, user, goToEditMode),
    [d, user, goToEditMode]
  );
  const nestedReplyCount = useMemo(() => getNestedReplyCount(d), [d]);

  return (
    <Container id={toCommentIdHash(d.id)}>
      <Header>
        <CommentCreator d={d} />
        <HeaderSection>
          <AdditionalActionsMenu
            options={options}
            variant="horizontal"
            maxWidth={340}
          />
          {d.data.pinned && <Pin />}
        </HeaderSection>
      </Header>
      <Body>
        <InnerBody>
          {editMode && user ? (
            <EditForm
              user={user}
              commentId={d.id}
              originalBody={d.data.body}
              onSuccess={() => setEditMode(false)}
              onCancel={() => setEditMode(false)}
            />
          ) : d.data.status === CommentStatus.REMOVED ? (
            <RemovedBody d={d} />
          ) : (
            <ReactMarkdown
              source={d.data.body}
              renderers={{
                heading: (props) => (
                  <Typography
                    variant={`h${props.level - 3 > 6 ? props.level - 3 : 6}`}
                    component="p"
                    {...props}
                  />
                ),
                blockquote: (props) => <Blockquote {...props} />,
                link: (props) => <LinkExternal underline {...props} />
              }}
            />
          )}
        </InnerBody>
        {!editMode && <ReplyButton onClick={() => setShowReplyForm(true)} />}
        <Replies>
          <Comments ds={d.data.replies} entityRef={entityRef} />
        </Replies>

        {nestedReplyCount > 1 && !showReplyForm && (
          <ReplyButton onClick={() => setShowReplyForm(true)} />
        )}

        {showReplyForm && (
          <ReplyForm
            entityRef={entityRef}
            parentRef={parentRef}
            onSuccess={() => setShowReplyForm(false)}
            onCancel={() => setShowReplyForm(false)}
            autoFocus
          />
        )}
      </Body>
    </Container>
  );
};

export const CommentFeed: React.FC<Props> = ({ entityRef }) => {
  const { user } = useCurrentUser();
  const [comments, loading, error] = useCommentHierarchyForEntity(
    entityRef,
    !!user?.isAdmin
  );
  if (loading) {
    return <PlainLoader height={200} />;
  }
  if (error) {
    return null;
  }
  if (comments) {
    return (
      <Comments
        ds={comments}
        entityRef={entityRef}
        scrollTo={window.location.hash.replace('#', '')}
      />
    );
  }
  return null;
};
